// ブロック入札の承認確認 (PENDING → APPROVED、 AC-BSP-094 / AC-BSP-097) の確認 modal に載せる
// 入札制限違反明細の組み立て (= 純関数)。 通常入札の `spotApprovalDecision.ts` の
// `buildSpotViolationSections` と対で、 サーバー (BlockBidUsecase.ensureWithinBidLimitsAtTransition)
// が承認時に拒否する 3 軸 (価格の上下限・上限数量(1札)・上限数量(コマ計)) を modal 内に出す。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingBlockApprovalRequest,
  BiddingBlockBid,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'
import { convertAreaLabel } from '~/models/area'
import type { AreaSideLimit, BidLimits } from '~/models/bidLimits'
import {
  checkBidLimit,
  perBidVolumeLimitExceeded,
  resolveAreaSideLimit,
} from '~/models/bidLimits'

import { blockGroupLegs } from './blockGroupLegs'
import { hasVolumeTotalLimit, stairsKeyOf } from './spotApprovalDecision'
import type {
  LimitViolation,
  VolumeCountableBid,
  VolumeTotalViolation,
} from './spotBidValidation'
import {
  detailHead,
  detectVolumeTotalViolations,
  formatLimitPriceDetail,
  formatLimitViolationDetail,
  formatVolumeTotalViolationDetail,
  sideLabelOf,
} from './spotBidValidation'

const slotsOf = (leg: BiddingBlockBid): BiddingBlockBid['timeSlots'] =>
  [...leg.timeSlots].sort((a, b) => a.timeCode - b.timeCode)

/** 価格違反は leg 単位 (価格は leg の全コマで 1 値) なので、 コマ範囲を持って 1 件にまとめる。 */
export type BlockPriceViolation = {
  kind: 'price-min' | 'price-max'
  areaCode: AreaCode
  side: Side
  timeCodeFrom: number
  timeCodeTo: number
  /** 実際の入札価格 (円/kWh)。 */
  actual: number
  limit: AreaSideLimit
}

/**
 * 承認対象の group を (エリア × 売買) の入札制限と照合する。 サーバーの
 * `ensureWithinBlockBidLimits` と同じ軸 (下限価格・上限価格・上限数量(1札)) を見る。
 * 制限未設定の (エリア × 売買) は違反なし。 価格はコマ範囲全体で単一値なので leg ごとに
 * 1 件、 数量はコマ別 (AC-BSP-030a) なので超過したコマごとに 1 件を返す。
 */
export const detectBlockLimitViolations = (
  group: BiddingBlockApprovalRequest,
  limits: BidLimits,
): { price: BlockPriceViolation[]; volume: LimitViolation[] } => {
  const price: BlockPriceViolation[] = []
  const volume: LimitViolation[] = []
  for (const leg of blockGroupLegs(group)) {
    const limit = resolveAreaSideLimit(limits, leg.areaCode, leg.direction)
    const slots = slotsOf(leg)
    const first = slots[0]
    const last = slots[slots.length - 1]
    if (limit === undefined || first === undefined || last === undefined) {
      continue
    }
    if (leg.priceYenPerKwh !== undefined) {
      const legPrice = Number(leg.priceYenPerKwh.value)
      for (const kind of checkBidLimit(legPrice, undefined, limit)) {
        if (kind === 'volume-max') continue
        price.push({
          kind,
          areaCode: leg.areaCode,
          side: leg.direction,
          timeCodeFrom: first.timeCode,
          timeCodeTo: last.timeCode,
          actual: legPrice,
          limit,
        })
      }
    }
    for (const slot of slots) {
      const volumeMw = Number(slot.volumeMwh?.value ?? '0')
      if (
        perBidVolumeLimitExceeded(
          limits,
          leg.areaCode,
          leg.direction,
          volumeMw,
        ) === undefined
      ) {
        continue
      }
      volume.push({
        bidId: leg.id,
        kind: 'volume-max',
        areaCode: leg.areaCode,
        side: leg.direction,
        timeCode: slot.timeCode,
        actual: volumeMw,
        limit,
      })
    }
  }
  return { price, volume }
}

const formatBlockPriceViolationDetail = (
  v: BlockPriceViolation,
  t: TFunction,
): string =>
  formatLimitPriceDetail(
    v.timeCodeFrom === v.timeCodeTo
      ? detailHead(v.timeCodeFrom, v.areaCode, v.side)
      : t('page:biddingBlock.violation.legHead', {
          from: v.timeCodeFrom,
          to: v.timeCodeTo,
          area: convertAreaLabel(v.areaCode),
          side: sideLabelOf(v.side),
        }),
    v.actual,
    v.limit,
  )

/**
 * 承認確認 modal に積む違反明細セクション (= 検出結果のみ)。 通常入札と同じく
 * 入札制限違反 (価格・上限数量(1札)) と上限数量(コマ計) を別セクションで出す (AC-BSP-063)。
 * 上限数量(1札) の行は AC-BSP-063 の承認操作時の規定文言 (`formatLimitViolationDetail` の
 * approve)。
 *
 * 上限数量(コマ計) は同一 (エリア × コマ × 売買) の集計判定で、 母集合 `volumeTotalRows`
 * は呼び出し元が受渡日の有効札全体 (通常入札の `selectRevalidationBids` + ブロック入札の
 * `selectRevalidationBlockVolumes`。 承認対象の group 自身は PENDING なので後者に含まれる) で
 * 組んで渡す。 検出したバケットのうち**承認対象の group のコマが属するもの**だけを残す
 * (= 対象と無関係なコマの超過で承認を止めない。 サーバーも group 自身の leg が触るバケット
 * だけを見る)。 文言は AC-BSP-063 の承認操作時の規定文言を使い、 AC-BSP-097 が違反行に
 * 求める「対象エリア」 のため、 コマ・エリア・売買を前置する (1 group が複数コマ・複数
 * エリアに跨るため、 位置が無いと超過したバケットを区別できない)。
 */
export const buildBlockViolationSections = (
  group: BiddingBlockApprovalRequest,
  limits: BidLimits,
  volumeTotalRows: readonly VolumeCountableBid[],
  t: TFunction,
  /**
   * `volumeTotalRows` から検出済みの上限数量(コマ計) 超過。 渡すと母集合の再集計を省く
   * (= 数千グループを同じ母集合で集計し直さないため。 まとめて承認が案件ごとに呼ぶ)。
   */
  volumeTotalViolations?: readonly VolumeTotalViolation[],
): ViolationSection[] => {
  const { price, volume } = detectBlockLimitViolations(group, limits)
  const limitDetails = [
    ...price.map((v) => formatBlockPriceViolationDetail(v, t)),
    ...volume.map((v) => formatLimitViolationDetail(v, 'approve')),
  ]
  const scope = new Set(
    blockGroupLegs(group).flatMap((leg) =>
      leg.timeSlots.map((slot) =>
        stairsKeyOf(slot.timeCode, leg.areaCode, leg.direction),
      ),
    ),
  )
  const volumeTotalDetails = (
    volumeTotalViolations ??
    detectVolumeTotalViolations(volumeTotalRows, limits)
  )
    .filter((v) => scope.has(stairsKeyOf(v.timeCode, v.areaCode, v.side)))
    .sort(
      (a, b) =>
        a.timeCode - b.timeCode || a.areaCode - b.areaCode || a.side - b.side,
    )
    .map((v) =>
      t('page:biddingBlock.violation.volumeTotalDetail', {
        head: detailHead(v.timeCode, v.areaCode, v.side),
        detail: formatVolumeTotalViolationDetail(v, 'approve'),
      }),
    )
  return [
    {
      title: t('page:biddingSpot.violation.limitTitle', {
        count: limitDetails.length,
      }),
      body: t('page:biddingSpot.violation.limitBodyApprove'),
      details: limitDetails,
    },
    {
      title: t('page:biddingSpot.violation.volumeTotalTitle', {
        count: volumeTotalDetails.length,
      }),
      body: t('page:biddingBlock.violation.volumeTotalBodyApprove'),
      details: volumeTotalDetails,
    },
  ]
}

/**
 * 入札制限 (settings「入札制限」) を取得できていない時に積む fail-closed セクション。
 * 通常入札の `buildBidLimitsUnavailableSection` (承認宛先) と同じ理由で、 空 lookup の
 * 「違反なし」 と「検証できていない」 を区別して確定ボタンを止める。
 */
export const buildBlockLimitsUnavailableSection = (
  t: TFunction,
): ViolationSection => ({
  title: t('page:biddingSpot.violation.limitUnavailableApproveTitle'),
  body: t('page:biddingBlock.violation.limitUnavailableBody'),
  details: [t('page:biddingBlock.violation.limitUnavailableDetail')],
})

/**
 * 通常入札を確定できていない時に積む、 上限数量(コマ計) の fail-closed セクション。
 * 母集合には有効な通常入札の当該コマ数量が入る (AC-BSP-080)。 通常入札の一覧・明細を
 * 確定できない間にそのまま判定すると、 通常入札分を 0 として「違反なし」 に見えるだけで
 * 検証が成立しない。 承認対象の group のコマが上限数量(コマ計) の設定された
 * (エリア × 売買) に 1 つでも属する時だけ節を立てる (設定が無ければ通常入札分の有無で
 * 結論が変わらない。 通常入札側の `buildBlockVolumesUnavailableSections` と対)。
 */
export const buildSpotVolumesUnavailableSections = (
  group: BiddingBlockApprovalRequest,
  limits: BidLimits,
  t: TFunction,
): ViolationSection[] => {
  if (!hasVolumeTotalLimit(blockGroupLegs(group), limits)) return []
  return [
    {
      title: t('page:biddingBlock.violation.spotVolumeUnavailableTitle'),
      body: t('page:biddingBlock.violation.spotVolumeUnavailableBody'),
      details: [t('page:biddingBlock.violation.spotVolumeUnavailableDetail')],
    },
  ]
}

/** 承認確認の違反明細の組み立てに要る、 案件に依らない前提。 */
export type BlockApprovalCheckContext = {
  limits: BidLimits
  /** 上限数量(コマ計) の再検証母集合 (通常入札 + ブロック入札の当該コマ数量)。 */
  volumeTotalRows: readonly VolumeCountableBid[]
  /** `volumeTotalRows` から検出済みの超過 (省略時は案件ごとに検出する)。 */
  volumeTotalViolations?: readonly VolumeTotalViolation[]
  /** 入札制限が取得中または取得失敗か (= 再検証が成立しないので fail-closed)。 */
  isLimitsUnavailable: boolean
  /** 通常入札の一覧 / 明細が未解決か (= 上限数量(コマ計) が fail-closed)。 */
  isSpotVolumeUnresolved: boolean
}

/**
 * 承認確認 (PENDING → APPROVED、 AC-BSP-097) で 1 つのグループに積む違反明細と判定できない節。
 * 単件の承認確認 modal (`useBlockApprovalDecision`) と、 案件ごとに同じ検証を見せるまとめて承認
 * (AC-BSP-118) が共有する唯一の組み立て。 判定できない節を分けて返す理由は
 * `buildSpotApprovalChecks` と同じ。
 */
export const buildBlockApprovalChecks = (
  group: BiddingBlockApprovalRequest,
  ctx: BlockApprovalCheckContext,
  t: TFunction,
): {
  violationSections: ViolationSection[]
  unverifiedSections: ViolationSection[]
} => ({
  violationSections: buildBlockViolationSections(
    group,
    ctx.limits,
    ctx.volumeTotalRows,
    t,
    ctx.volumeTotalViolations,
  ),
  unverifiedSections: [
    // 入札制限を取得できていない間は価格・数量の判定が空 lookup になり「違反なし」 と
    // 「検証できていない」 が区別できない。 通常入札と同じく fail-closed にする。
    ...(ctx.isLimitsUnavailable ? [buildBlockLimitsUnavailableSection(t)] : []),
    ...(ctx.isSpotVolumeUnresolved
      ? buildSpotVolumesUnavailableSections(group, ctx.limits, t)
      : []),
  ],
})
